import React from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';

import Box from '@mui/material/Box';

import LocationsCustomMessage from 'bento-ordering/locations/components/LocationsCustomMessage';
import GridContainer from 'bento-ordering/base/ui_components/GridContainer';
import Footer from 'components/Footer';
import SecondaryCta from 'components/Buttons/SecondaryCta';
import MenuPageFooter from 'bento-ordering/menus/containers/MenuPageFooter';
import { getCustomMessageSelector } from 'bento-ordering/account/reducers';
import {
  isSoleEntity,
  getItemByIdSelector
} from 'bento-ordering/base/reducers/bentoApi';
import MenuSectionsBar from 'bento-ordering/menus/containers/MenuSectionsBar';
import {
  DEFAULT_LAYOUT_PADDING,
  MENU_LAYOUT_PADDING
} from 'styles/constants/layoutPadding';
import { MAX_MENU_WIDTH } from 'bento-ordering/base/constants/maxWidth';
import { CHILDREN_PROP_TYPES } from 'propTypes/childrenPropTypes';
import {
  getCustomLayoutHero,
  getCustomLayoutHeroWithoutImage
} from 'bento-ordering/base/constants/customLayouts';
import selectLayout from 'bento-ordering/base/selectors/selectLayout';
import isDineInApplicationVariant from 'bento-ordering/utils/isDineInApplicationVariant';
import { useIsMobile } from 'styles/hooks/mediaQueries';
import {
  getButtonBackgroundHoverColor,
  getItemDescriptionsAndPricesTypography
} from 'styles/helpers/getThemeStyles';
import PromotionCardRow from 'bento-ordering/promotions/components/PromotionCardRow';
import CreateGroupOrderMobileCTA from 'bento-ordering/group_ordering/components/CreateGroupOrderMobileCTA';
import { useIsGroupOrderGuestDiner } from 'bento-ordering/group_ordering/hooks';
import {
  useMenu,
  FILTERED_ITEM_NAV_BAR,
  MENU_NAV_BAR
} from 'bento-ordering/context/MenuContext';
import FilteredItemBar from 'bento-ordering/promotions/components/FilteredItemBar';
import { useHasInvestorDayFeatureFlag } from 'bento-ordering/utils/featureFlags/hasInvestorDayFeatureFlag';
import HonestPricingLawBanner from 'base-ordering/components/HonestPricingLawBanner';
import useIsCaliforniaLocationWithServiceFee from 'bento-ordering/hooks/useIsCaliforniaLocationWithServiceFee';
import { mapCartToServiceFeeType } from 'bento-ordering/utils/HonestPricingLaw';

function ScreenHero({ hideHeroOnMobile, heroImage, heroContent }) {
  const isMobile = useIsMobile();
  const layout = useSelector(selectLayout);

  const shouldHideOnMobile = isMobile && hideHeroOnMobile;

  const hasHeroImage = !!heroImage?.url;
  const hasHeroContent = !!heroContent;
  const shouldDisplayHero = hasHeroImage || hasHeroContent;

  // couple of cases where we don't display hero
  // 1) if current viewport is mobile, and the prop has been set to hide on mobile
  // 2) if there is no hero content, which can include an image, or other specified
  // hero content (like a dom node containing a search bar on location list page)
  if (shouldHideOnMobile) {
    return null;
  }

  if (!shouldDisplayHero) {
    const LayoutWithoutHeroImage = getCustomLayoutHeroWithoutImage(layout);
    if (!LayoutWithoutHeroImage) {
      return null;
    }

    return <LayoutWithoutHeroImage />;
  }

  const Hero = getCustomLayoutHero(layout);

  return <Hero heroImage={heroImage} heroContent={heroContent} />;
}

ScreenHero.propTypes = {
  hideHeroOnMobile: PropTypes.bool,
  heroImage: PropTypes.object,
  heroContent: CHILDREN_PROP_TYPES
};

function ScreenMenuCustomText({
  shouldUseMenuStyle,
  customMessage,
  isSingleLocation
}) {
  if (shouldUseMenuStyle) {
    if (customMessage && isSingleLocation) {
      return (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <Box
            sx={{
              width: MAX_MENU_WIDTH,
              borderBottom: '1px solid #E5E5E6'
            }}
          >
            <Box
              sx={{
                pt: 3,
                ml: 2,
                mr: 2,
                textAlign: 'center',
                maxWidth: MAX_MENU_WIDTH
              }}
            >
              <LocationsCustomMessage message={customMessage} />
            </Box>
          </Box>
        </Box>
      );
    }

    return null;
  }

  return null;
}

ScreenMenuCustomText.propTypes = {
  shouldUseMenuStyle: PropTypes.bool,
  customMessage: PropTypes.string,
  isSingleLocation: PropTypes.bool
};

function Screen({
  hideHeroOnMobile,
  heroImage,
  heroContent,
  hideBrandFooterOnMobile,
  isFetching,
  shouldUseMenuStyle,
  mobileFooter,
  hideMobileStickyCtaFooter,
  layoutPadding,
  id,
  children,
  isModal
}) {
  const account = useSelector(state => state.account);
  const restaurantLocations = useSelector(state => state.restaurantLocations);
  const isDineInVariant = isDineInApplicationVariant();
  const { utm_footer_link: utmFooterLink = '' } = account;
  const { menuNavType } = useMenu();

  const customMessage = getCustomMessageSelector(account);
  const isSingleLocation = isSoleEntity(restaurantLocations);
  const padding = shouldUseMenuStyle ? MENU_LAYOUT_PADDING : layoutPadding;
  const shouldRender = !isFetching && shouldUseMenuStyle;
  const shouldHidePromotionCardRow = useIsGroupOrderGuestDiner();
  const hasInvestorDayFeature = useHasInvestorDayFeatureFlag();
  const shouldRenderCaliforniaServiceFee = useIsCaliforniaLocationWithServiceFee();
  const cart = useSelector(state => state.cart);
  const {
    display_service_fee: displayServiceFee,
    online_ordering_fulfillment_method: onlineOrderingFulfillmentMethod,
    is_group_order: isGroupOrder,
    online_ordering_location: onlineOrderingLocation
  } = cart;
  const selectedLocation = getItemByIdSelector(
    restaurantLocations,
    onlineOrderingLocation
  );
  const {
    charge_custom_fee: chargeCustomFee,
    charge_service_fee: chargeServiceFee,
    custom_fee_amount: customFeeAmount,
    custom_fee_flat_or_percent: customFeeFlatOrPercent,
    custom_fee_name: customFeeName
  } = selectedLocation;

  const { bannerFeeText } = mapCartToServiceFeeType(
    displayServiceFee,
    onlineOrderingFulfillmentMethod,
    isGroupOrder,
    chargeCustomFee,
    chargeServiceFee,
    customFeeName,
    customFeeAmount,
    customFeeFlatOrPercent
  );

  const ownFooterState = theme => {
    return {
      color: getButtonBackgroundHoverColor(theme),
      ...getItemDescriptionsAndPricesTypography(theme)
    };
  };

  return (
    <>
      {!isModal && (
        <ScreenHero
          hideHeroOnMobile={hideHeroOnMobile}
          heroImage={heroImage}
          heroContent={heroContent}
        />
      )}
      <ScreenMenuCustomText
        shouldUseMenuStyle={shouldUseMenuStyle}
        customMessage={customMessage}
        isSingleLocation={isSingleLocation}
      />
      {!hasInvestorDayFeature &&
        shouldUseMenuStyle &&
        menuNavType === MENU_NAV_BAR && <MenuSectionsBar />}
      {!isDineInVariant && shouldUseMenuStyle && <CreateGroupOrderMobileCTA />}
      <GridContainer {...padding}>
        {shouldRender && menuNavType === FILTERED_ITEM_NAV_BAR && (
          <FilteredItemBar />
        )}
        {shouldRenderCaliforniaServiceFee && (
          <HonestPricingLawBanner bannerFeeText={bannerFeeText} />
        )}
        {shouldRender &&
          menuNavType === MENU_NAV_BAR &&
          !shouldHidePromotionCardRow && <PromotionCardRow />}
        <div id={id}>{children}</div>
        {shouldRender && (
          <Footer hideBrandFooterOnMobile={hideBrandFooterOnMobile}>
            <SecondaryCta
              label="Powered By Bentobox"
              component="a"
              href={utmFooterLink}
              dataTestId="footer"
              // TODO: look into leveraging typography props for buttons
              ownState={{
                footer: ownFooterState
              }}
            />
          </Footer>
        )}
        {!hideMobileStickyCtaFooter && !isFetching ? mobileFooter : null}
      </GridContainer>
    </>
  );
}

Screen.propTypes = {
  hideHeroOnMobile: PropTypes.bool,
  heroImage: PropTypes.object,
  heroContent: CHILDREN_PROP_TYPES,
  hideBrandFooterOnMobile: PropTypes.bool,
  isFetching: PropTypes.bool,
  shouldUseMenuStyle: PropTypes.bool,
  mobileFooter: CHILDREN_PROP_TYPES,
  hideMobileStickyCtaFooter: PropTypes.bool,
  layoutPadding: PropTypes.object,
  id: PropTypes.string,
  children: CHILDREN_PROP_TYPES.isRequired,
  isModal: PropTypes.bool
};

Screen.defaultProps = {
  mobileFooter: <MenuPageFooter />,
  layoutPadding: DEFAULT_LAYOUT_PADDING,
  shouldUseMenuStyle: false
};

export default Screen;
